import { create } from 'zustand';

/**
 * Tabs available in `AppSettingsPanel`. Keep in sync with the Panel's
 * internal `SettingsTab` union — exported here so callers (DesktopIcon,
 * Banner, KMenu, ...) can request a specific tab when opening Settings.
 *
 * Legacy tabs (`status`, `service`) are accepted by callers but mapped to
 * `runtime`
 * inside the panel for backward compatibility.
 */
export type AppSettingsTab =
	| 'about'
	| 'runtime'
	| 'providers'
	| 'status'
	| 'environment'
	| 'logs'
	| 'control'
	| 'usage'
	| 'service'
	| 'integrations';

interface OpenSettingsRequest {
	appId: string;
	tab: AppSettingsTab;
	/** Monotonic counter so consumers can react even if the same (appId, tab)
	 *  is requested twice in a row. */
	nonce: number;
}

interface AppSettingsStore {
	current: OpenSettingsRequest | null;
	openAppSettings: (appId: string, tab?: AppSettingsTab) => void;
	closeAppSettings: () => void;
}

let nonceCounter = 0;

/**
 * Cross-component bus for "open app X's Settings panel on tab Y". Used by:
 *   - DesktopIcon (NEEDS_CONFIG click → open Environment tab)
 *   - LifecycleBanner ("Configure now" → open Environment tab)
 *   - Future callers (KMenu, command palette, etc.)
 *
 * Consumers (the AppSettingsPanel host — usually a Window child) subscribe
 * to `current` and render the panel for the matching `appId`. The shell
 * orchestrator owns the actual mount.
 */
export const useAppSettingsStore = create<AppSettingsStore>((set) => ({
	current: null,
	openAppSettings: (appId, tab = 'about') => {
		nonceCounter += 1;
		set({ current: { appId, tab, nonce: nonceCounter } });
	},
	closeAppSettings: () => set({ current: null }),
}));
